import axios from 'axios';
import { theFirebase } from '../services/Firebase/firebase';
import { strogging } from '@shd/jslib/infra';
import * as Sentry from '@sentry/browser';
import useSWR, { SWRConfiguration, SWRResponse } from 'swr';
import isSidelineRequest from '@helpers/sidelineRequest';

const instance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  timeout: 30 * 1000,
});

instance.interceptors.request.use(async (config) => {
  let token;
  let scoringKey;

  if (!config.url?.includes('send_telemetry')) {
    strogging.log('api-request', { url: config.url, method: config.method });
  }

  try {
    token = await theFirebase().auth.currentUser?.getIdToken();
  } catch (error) {
    strogging.exception('axios-interceptor:exception', error);
  }

  // Check if a custom scoring key is provided in the request config
  if (config.headers['X-Slhd-Scoring-Key']) {
    scoringKey = config.headers['X-Slhd-Scoring-Key'];
  }

  const configCopy = { ...config };

  // Prioritize Firebase Auth token, but allow scoring key fallback
  if (token) {
    configCopy.headers.Authorization = `Bearer ${token}`;
  } else if (scoringKey) {
    configCopy.headers['X-Slhd-Scoring-Key'] = scoringKey;
  }

  return configCopy;
});

export type StandardHook<T> = SWRResponse<T, Error>;

export function useGetApi<TResponse>(
  key: string | null | undefined,
  config?: SWRConfiguration<TResponse>
): StandardHook<TResponse> {
  return useSWR<TResponse, Error, string | null>(
    key ?? null,
    (k) => instance.get<TResponse>(k).then((r) => r.data),
    config
  );
}

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const rejected = Promise.reject(error); // forwards the error on return

    if (error.response) {
      const { status, data, headers, config } = error.response;
      const { method, url } = error.config;

      const requestError = status >= 400 && status < 499;

      // don't send 4xx responses from sideline to Sentry
      if (isSidelineRequest(config.baseURL) && requestError) {
        return rejected;
      }

      // TODO: Add other conditions under which we **should not** report to Sentry

      Sentry.captureException(error, {
        extra: {
          request: {
            method,
            url,
            headers: error.config.headers,
          },
          response: {
            status,
            data,
            headers,
          },
        },
      });
    } else if (error.request) {
      Sentry.captureException(error, {
        extra: {
          request: {
            headers: error.config.headers,
            method: error.config.method,
            url: error.config.url,
          },
          message: 'No response received from server',
        },
      });
    } else {
      Sentry.captureException(error, {
        extra: {
          message: error.message,
        },
      });
    }

    return rejected;
  }
);

export default instance;
